import { Button, Group, Loader, Modal, Stack, Table, Text } from '@mantine/core';
import { MarkdownView } from 'src/common/components/markdown-editor/MarkdownView';
import { formatSkillName } from 'src/common/utils/skillNames';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { formatFileSize } from 'src/utils/fileSizeFormatting';
import AddToCompanyButton from './AddToCompanyButton';
import useSkillLibraryEntry from './hooks/useSkillLibraryEntry';
import type { SkillLibraryEntryListItem } from './types';

type SkillPreviewModalProps = {
  entry: SkillLibraryEntryListItem | null;
  companyName: string | null;
  onClose: () => void;
};

export default function SkillPreviewModal({ entry, companyName, onClose }: SkillPreviewModalProps) {
  const { data: skill, isLoading, isError } = useSkillLibraryEntry(entry?.id ?? null);
  const { flagEnabledSiestaSuitabilityReportProposals } = useFlags();

  return (
    <Modal
      opened={entry !== null}
      onClose={onClose}
      title={skill ? formatSkillName(skill.name) : 'Skill'}
      size="xl"
      centered
    >
      {isLoading && <Loader size="sm" />}
      {isError && (
        <Text c="red" role="alert">
          Couldn&apos;t load this skill. Please try again.
        </Text>
      )}
      {skill && entry && (
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {skill.description}
          </Text>
          {flagEnabledSiestaSuitabilityReportProposals === true &&
            skill.canGenerateSuitabilityReports && (
              <Text size="sm" fw={500}>
                Can propose suitability reports
              </Text>
            )}
          <MarkdownView value={skill.markdown} />
          {skill.files.length > 0 && (
            <Stack gap="xs">
              <Text fw={500} size="sm">
                Supporting files
              </Text>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Size</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {skill.files.map(file => (
                    <Table.Tr key={file.filename}>
                      <Table.Td>{file.filename}</Table.Td>
                      <Table.Td>{formatFileSize(file.sizeBytes)}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Stack>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Close
            </Button>
            <AddToCompanyButton entry={entry} companyName={companyName} />
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
